<template>
  <div class="clearfix" @mouseover="hover = true" @mouseleave="hover = false">
    <div :class="{ error: validationErrors.length, 'form-group': true }">
      <div v-if="locked">
        <locked-category-icon />
        <label for="chapter-selector">
          {{ lang.get('entries.form.chapter.label') }}
        </label>
        <settings
          :add-icon-visible="addIconVisible"
          :edit-icon-visible="editIconVisible"
          @add="insertChapter"
          @edit="onConfigure"
        />
        <select-field
          id="chapter-selector"
          :items="items"
          :value="chapterId"
          disabled="true"
        />
      </div>
      <div v-else>
        <div v-if="multiChapterEnabled && (hasMany || configurationMode)">
          <label for="chapter-selector">
            {{ lang.get('entries.form.chapter.label') }}
          </label>
          <settings
            :add-icon-visible="addIconVisible"
            :edit-icon-visible="editIconVisible"
            @add="insertChapter"
            @edit="onConfigure"
          />
          <select-field
            id="chapter-selector"
            name="chapterId"
            :items="items"
            :value="chapter"
            @selected="onSelect"
          />
        </div>
      </div>
      <validation-errors :validation-errors="validationErrors" />
    </div>
    <confirmation-modal
      v-if="chapterId"
      :key="`modal-target-configure-chapter-${chapterId}`"
      :modal-id="`modal-target-configure-chapter-${chapterId}`"
      :show-modal="confirmConfiguration"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmConfiguration = false"
      @confirmed="configure"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { SelectField } from 'vue-bootstrap';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import ValidationErrors from '@/lib/components/Fields/ValidationErrors.vue';
import LockedCategoryIcon from './LockedCategoryIcon';
import Settings from './Settings';

export default {
  inject: ['lang', 'featuresService'],
  components: {
    ConfirmationModal,
    SelectField,
    LockedCategoryIcon,
    ValidationErrors,
    Settings
  },
  data() {
    return {
      hover: false,
      confirmConfiguration: false
    };
  },
  computed: {
    ...mapGetters('entryForm', ['selectedChapter']),
    ...mapGetters('entryFormConfiguration', ['configurationActive']),
    ...mapState('entryForm', [
      'chapterId',
      'chapters',
      'locks',
      'hasManagerRole',
      'chaptersLoaded',
      'chapterTemplate',
      'canConfigureChapters'
    ]),
    ...mapState('entryFormApi', ['errorBag']),
    ...mapState('entryFormConfiguration', [
      'configurationMode',
      'configuredCategory',
      'configuredChapter',
      'configurationInProgress'
    ]),
    chapter: {
      get() {
        return this.chapterId;
      },
      set(id) {
        this.selectChapter(id);
      }
    },
    locked() {
      return (
        this.locks.readOnly ||
        this.locks.lockedCategory ||
        this.configuredCategory ||
        this.configuredChapter ||
        (!this.chaptersLoaded && this.hasMany)
      );
    },
    hasMany() {
      return this.chapters.length > 1;
    },
    validationErrors() {
      return this.errorBag && this.errorBag.chapterId
        ? this.errorBag.chapterId.message
        : [];
    },
    items() {
      return this.chapters.map(chapter => ({
        id: chapter.id,
        name: this.displayName(chapter)
      }));
    },
    editIconVisible() {
      return !!(
        this.hover &&
        this.chaptersLoaded &&
        this.chapter &&
        this.canConfigureChapters &&
        !this.configuredChapter
      );
    },
    addIconVisible() {
      return !!(
        this.hover &&
        this.chaptersLoaded &&
        this.canConfigureChapters &&
        !this.configurationActive
      );
    }
  },
  methods: {
    ...mapActions('entryForm', ['selectChapter']),
    ...mapActions('entryFormConfiguration', ['configureChapter']),
    ...mapMutations('entryForm', ['addChapter', 'updateChapterId']),
    onSelect(name, selected) {
      this.chapter = selected;
    },
    onConfigure() {
      if (this.configurationInProgress) {
        this.confirmConfiguration = true;
      } else {
        this.configure();
      }
    },
    multiChapterEnabled() {
      return this.featuresService.enabled('multi_chapter');
    },
    configure() {
      if (this.selectedChapter) {
        this.configureChapter({ slug: this.selectedChapter.slug });
      }
    },
    displayName(chapter) {
      if (!this.hasManagerRole || chapter.name === '-') {
        return chapter.name;
      }

      if (!chapter.active) {
        return `${chapter.name} ${this.lang.get('entries.form.inactive')}`;
      }

      if (!chapter.inActiveRound) {
        return `${chapter.name} ${this.lang.get('entries.form.round_closed')}`;
      }

      return chapter.name;
    },
    insertChapter() {
      const slug = 'newchapter';
      const chapter = { ...this.chapterTemplate, slug };

      this.addChapter(chapter);
      this.updateChapterId(chapter.id);
      this.configureChapter({ slug });
    }
  }
};
</script>
